import {ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {ILabels} from "libraries/interfaces/ILabels";
import {Yii} from "libraries/interfaces/Yii";
import {LanguageService} from "libraries/service/language/language.service";
import {EUnitAbbr, ICarbonFootPrint, ICarbonFootPrintDifference} from "./carbon-footprint.interface";

@Component({
    selector: 'carbon-footprint',
    template: `
        <ng-container *permissionIf="'shared.air.carbonFootprint.show'">
            <div class="CO2-container" [ngClass]="classList" [class.container]="alignWithPrice">
                <span [class.label]="alignWithPrice">{{wording.co2}}</span>
                <div [class.content]="alignWithPrice" [class.CO2-values]="!alignWithPrice">
                    <ng-container *ngIf="carbonValue && carbonValue.value; else noCarbon">
                        <strong>{{carbonValue.value | number: '1.0-0': language}} {{unitAbbr}}</strong>
                        <div *ngIf="carbonDifference && !carbonDifference.isEqual">
                            <strong class="carbon-difference"
                                    [class.is-superior]="carbonDifference.isSuperior">
                                {{carbonDifference.isSuperior ? "+" : "-"}}{{carbonDifference.value | number: '1.0-0': language}} {{unitAbbr}}
                            </strong>
                        </div>
                    </ng-container>
                </div>
            </div>
            <ng-template #noCarbon>
                {{wording.noCarbon}}
            </ng-template>

        </ng-container>
    `,
    styles: [
        `
            .CO2-container {
                display: flex;
            }

            .CO2-container.align-right {
                justify-content: flex-end;
            }

            span, strong {
                white-space: nowrap;
            }

            .CO2-values {
                text-align: end;
                margin-left: .25rem;
                min-width: 40px;
            }

            .carbon-difference {
                color: #3c763d;
            }

            .carbon-difference.is-superior {
                color: #a94442;
            }

            .container {
                display: flex;
                align-items: center;
            }

            .content {
                display: block;
                width: 110px;
                text-align: right;
            }

            .label {
                flex-grow: 1;
                text-align: right;
                padding-right: .75rem;
            }

        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class CarbonFootprintComponent implements OnChanges, OnInit {

    @Input()
    public carbonValue: ICarbonFootPrint;

    @Input()
    public referenceValue: ICarbonFootPrint;

    @Input()
    public classList: string;

    @Input()
    public alignWithPrice?: boolean = false;

    public unitAbbr: EUnitAbbr;
    public carbonDifference: ICarbonFootPrintDifference = null;

    public readonly wording: ILabels = {
        noCarbon: Yii.t('wui-shared-js', 'Inconnu'),
        co2: 'CO\u2082 :'
    };

    public language: string = 'fr';

    constructor(
        private readonly languageService: LanguageService,
    ) {
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.carbonValue && changes.carbonValue.currentValue) {
            this.unitAbbr = EUnitAbbr[this.carbonValue.xUnit];
        }
        if (changes && changes.referenceValue && changes.referenceValue.currentValue) {
            this.initCarbonDifference(changes.referenceValue.currentValue);
        }
    }

    public ngOnInit(): void {
        this.language = this.languageService.getUserLanguage();
    }

    private initCarbonDifference(referenceValue: ICarbonFootPrint): void {
        const isSuperior: boolean = +this.carbonValue.value >= +referenceValue.value;
        const value: number = isSuperior ?
            +this.carbonValue.value - +referenceValue.value :
            +referenceValue.value - +this.carbonValue.value;
        this.carbonDifference = {
            value,
            isSuperior,
            isEqual: value === 0
        };
    }

}
